import { createRouter, createWebHistory, type RouteRecordRaw, type RouteLocationNormalized } from 'vue-router'
import LoginView from '@/views/auth/LoginView.vue';
import { AuthService } from '@/services/auth.ts'
import { toast } from '@/components/ui/toast'
import { trackRouteVisit } from '@/services/recent-pages.ts'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'Home',
    redirect: '/dashboard',
  },
  {
    path: '/about',
    name: 'About',
    component: () => import('../views/AboutView.vue'),
  },
  {
    path: '/login',
    name: 'Login',
    component: LoginView,
    meta: {
      requireAuth: false,
      requireGhost: true,
    },
  },
  {
    path: '/reset_password/',
    name: 'ResetPassword',
    component: LoginView,
    meta: {
      requireAuth: false,
      requireGhost: true,
    }
  },
  {
    path: '/sso/okta/callback',
    name: 'OktaCallback',
    component: () => import('../views/auth/OktaCallback.vue'),
    meta: {
      requireAuth: false,
      requireGhost: true,
    },
  },
  {
    path: '/sso/entra/callback',
    name: 'EntraCallback',
    component: () => import('../views/auth/EntraCallback.vue'),
    meta: {
      requireAuth: false,
      requireGhost: true,
    },
  },
  {
    // Deep link from bank systems (HubSpot, core, CRM): signs in through the
    // bank's SSO when needed, then opens the customer in the regular dashboard
    path: '/open/customer/:sso/:bankId/:idBank',
    name: 'OpenCustomer',
    component: () => import('../views/OpenCustomerView.vue'),
    meta: {
      requireAuth: false,
    },
  },
  {
    path: '/oauth/consent',
    name: 'OAuthConsent',
    component: () => import('../views/auth/OAuthConsentView.vue'),
    meta: {
      requireAuth: true,
    },
  },
  {
    path: '/experiment',
    name: 'Experiment',
    component: () => import('../views/DebugView.vue'),
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: () => import('../views/dashboard/DashboardView.vue'),
    children: [
      {
        path: '',
        name: 'HomeBoard',
        component: () => import('../views/dashboard/pages/HomePage.vue')
      },
      {
        path: 'account_engagement/',
        name: 'Account Engagement',
        component: () => import('../views/dashboard/pages/AccountEngagement.vue')
      },
      {
        path: 'categories/:id?',
        name: 'Categories',
        component: () => import('../views/dashboard/pages/CategoriesView.vue')
      },
      {
        path: 'audiences/intersection/:id?',
        name: 'Audiences Intersection',
        component: () => import('../views/dashboard/pages/AudienceIntersectionView.vue')
      },
      {
        path: 'audiences/v1/:id?',
        name: 'Audiences V1',
        component: () => import('../views/dashboard/pages/AudiencesView.vue')
      },
      {
        path: 'audiences/:id?',
        name: 'Audiences',
        component: () => import('../views/dashboard/pages/AudiencesV2View.vue')
      },
      {
        path: 'competitor/:id?',
        name: 'Competitor',
        component: () => import('../views/dashboard/pages/CompetitorDetailView.vue')
      },
      {
        path: ':entity(branch|relationship_manager)/:id?',
        name: 'Entity Card',
        component: () => import('../views/dashboard/pages/EntityDetailView.vue')
      },
      {
        path: 'customers/:id?',
        name: 'Customer',
        component: () => import('../views/dashboard/pages/CustomerView.vue'),
        beforeEnter: (to, from, next) => {
          if (!to.params.id) {
            toast({
              title: 'Customer ID Required',
              description: 'Please select a customer to view their details.',
              variant: 'default',
            })
            next('/dashboard')
          } else {
            next()
          }
        },
      },
      {
        path: 'account/:id?',
        name: 'Account',
        component: () => import('../views/dashboard/pages/AccountView.vue'),
      },
      {
        path: 'reviq/competitors/',
        name: 'ReviQ Competitors',
        component: () => import('../views/dashboard/pages/CompetitorsView.vue')
      },
      {
        path: 'signals/',
        name: 'Signals',
        component: () => import('../views/dashboard/pages/SignalsView.vue')
      },
      {
        path: 'exports/',
        name: 'Data Export',
        component: () => import('../views/dashboard/pages/ExportsView.vue')
      },
      {
        path: 'integrations/',
        name: 'FakeIntegrations',
        component: () => import('../views/dashboard/pages/IntegrationsView.vue')
      },
      {
        path: 'integrations/hubspot/',
        name: 'HubSpot Integration',
        component: () => import('../views/dashboard/pages/HubspotManageView.vue')
      },
      {
        path: 'integrations/creatio/',
        name: 'Creatio Integration',
        component: () => import('../views/dashboard/pages/CreatioManageView.vue')
      },
      {
        path: 'integrations/mcp/',
        name: 'MCP Integration',
        component: () => import('../views/dashboard/pages/MCPManageView.vue')
      },
      {
        path: 'engagement/',
        name: 'Engagement Manager',
        component: () => import('../views/dashboard/pages/EngagementManagerView.vue')
      },
      {
        path: 'methodology/',
        name: 'Audience-Targeting Methodology',
        component: () => import('../views/dashboard/pages/MethodologyView.vue')
      },
      {
        path: 'agent/:conversationId?',
        name: 'Revio Insight Agent',
        component: () => import('../views/dashboard/pages/AgentView.vue'),
        meta: { fullWidth: true }
      },
      {
        path: 'settings/',
        name: 'Settings',
        component: () => import('../views/settings/SettingsView.vue'),
        children: [
          {
            path: '',
            name: 'Account Settings',
            component: () => import('../views/settings/pages/AccountSettings.vue')
          },
          {
            path: 'team/',
            name: 'Team Management',
            component: () => import('../views/dashboard/pages/TeamManagement.vue')
          },
          {
            path: 'integrations/',
            name: 'Integrations',
            component: () => import('../views/settings/pages/IntegrationsView.vue')
          },
          {
            path: 'activity/',
            name: 'Activity Log',
            component: () => import('../views/settings/pages/ActivityLogView.vue')
          },
        ]
      }
    ],
  },
]

const authMiddleware = (to: RouteLocationNormalized, from: RouteLocationNormalized): string | undefined => {
  const requireAuth = to.meta.requireAuth ?? true
  const requireGhost = to.meta.requireGhost ?? false

  let redirectTo = null

  if (requireAuth && !AuthService.isAuthenticated()) {
    AuthService.setRedirectUrl(to.fullPath)
    redirectTo = '/login'
  }

  if (requireGhost && AuthService.isAuthenticated()) {
    redirectTo = '/'
  }

  if (redirectTo && redirectTo !== to.path) {
    return redirectTo
  }
}


const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: routes,
})

router.beforeResolve(async (to, from, next) => {
  const middlewares = [
    authMiddleware
  ]
  for (const middleware of middlewares) {
    const redirect = middleware(to, from)
    if (redirect) {
      return next(redirect)
    }
  }
  return next()
})

// "Continue your work": remember the dashboard pages the user opens (never blocks navigation)
router.afterEach((to) => trackRouteVisit(to))

export default router
